import queryString from 'query-string'

import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import filterFalsy from '@src/quince-site-common/src/utils/filterFalsy'

import { DYNAMIC_FEEDS, COLOR_PAGES_RENDERED_PATHS, VALENTINE_SLUGS } from './collection.constants'
import Collection, { SubCollection } from './domains/Collection'
import { SelectedFilter } from './domains/CollectionFilter'
import { ProductFeed } from './domains/ProductFeed'
import { IProductFeedResponseForCollection } from './services/collectionService/CollectionService.types'

export interface VariantList {
  product_id: number
  price: string
  labels: string
  variant_id: number
  index: number
}

export const isFeedDynamic = (slug: string) => DYNAMIC_FEEDS.includes(slug)

export function getBestSellerDataFromAPIProductFeed(
  subCollections: (SubCollection | CustomSection)[],
  productFeed: ProductFeed,
  merge?: boolean,
) {
  const outputSubCollections: (SubCollection | CustomSection)[] = []
  subCollections.forEach((subCollection, index) => {
    if (index === 0) {
      const updatedProductCards = [
        ...(merge ? (subCollection as SubCollection).productCards : []),
        ...(productFeed.productCards ?? []),
      ]
      outputSubCollections.push(
        new SubCollection({
          ...(subCollection as SubCollection),
          productCards: updatedProductCards,
        }),
      )
    } else {
      outputSubCollections.push(subCollection)
    }
  })
  return {
    subCollections: outputSubCollections,
    nextFeedURL: productFeed.nextURL,
  }
}

export const getNavigateTo = (selectedFilters: SelectedFilter, collectionPageSlug: string) => {
  if (COLOR_PAGES_RENDERED_PATHS.has(collectionPageSlug.slice(1))) {
    const appliedFilteredList = SelectedFilter.getSelectedValuesAsList(selectedFilters)
    const colorFilterSelectedValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilters, 'colors')
    const isOnlySingleColorFilterSelected = appliedFilteredList.length === 1 && colorFilterSelectedValues[0]
    if (!isOnlySingleColorFilterSelected) return null
    return `${collectionPageSlug}/color/${colorFilterSelectedValues[0]?.toLowerCase() ?? ''}`
  }
  return null
}

export const getQueryParamsForSelectedFilters = (
  selectedFilters: SelectedFilter,
  sortBy: string,
  _collectionPageSlug = '',
) => {
  const formattedSelectedFiltersString = SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(selectedFilters)
  const queryParams = {
    ...queryString.parse(window.location.search),
    ...(sortBy ? { sortBy: sortBy !== SORT_BY.FEATURED.value ? sortBy : undefined } : {}),
    filter: formattedSelectedFiltersString || undefined,
  }

  return queryParams
}

export const getVariantsListFromCollectionNew = (collection: (SubCollection | CustomSection)[]): VariantList[] => {
  let index = 0
  const variantList: VariantList[] = []

  for (const subCollection of collection) {
    if (subCollection.typename !== 'CustomSection' && subCollection.productCards) {
      variantList.push(
        ...filterFalsy(
          subCollection.productCards.map(({ product }) => {
            if ('type' in product && product.type === PRODUCT_TYPES.BUNDLE) {
              return null
            }
            const variant1 = product.cardVariants[0]
            const price = variant1?.price.salePrice
            if (!product.relatedIds.shopifyId) return false
            return {
              product_id: Number(product.relatedIds.shopifyId),
              price: `$${price}`,
              labels: product.title,
              variant_id: Number(variant1?.shopifyIds[0]),
              index: index++,
            }
          }),
        ),
      )
    }
  }
  return variantList
}

export const getTrackerUrlForSelectedFilters = (selectedFilters: SelectedFilter) => {
  let str = ''
  Object.keys(selectedFilters).forEach(sf => {
    const filterValues = selectedFilters[sf as keyof SelectedFilter]
    if (filterValues) {
      str += `__${sf}-${filterValues.join(',')}`
    }
  })
  return str
}

export const isValentineSlug = (slug: string) => {
  return VALENTINE_SLUGS.includes(slug)
}

const getProductKey = (productCard: IProductCard) => {
  const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
  return `${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`
}

function findFirstSubCollection(collectionPage: Collection) {
  return collectionPage.subCollections.find(sc => sc.typename === 'SubCollection') as SubCollection | undefined
}

function matchSubCollection(subCollectionA: SubCollection, subCollectionB: SubCollection) {
  return subCollectionA.title?.trim().toLowerCase() === subCollectionB.title?.trim().toLowerCase()
}

export const modifyFilteredCollectionOnProductFeed = (
  resp: IProductFeedResponseForCollection,
  newFilteredSubCollections: (SubCollection | CustomSection)[],
  page: number,
  slug: string,
  isInitialCall: boolean,
  collectionPage: Collection,
) => {
  const serverSubCollection = findFirstSubCollection(collectionPage)
  if (
    // this is for the collection types which have only one subcollection
    [
      'pickedForYou',
      'trendingThisWeek',
      'trending',
      'bestseller',
      'root',
      'newArrivals',
      'gift',
      'lastChance',
    ].includes(resp.collectionType)
  ) {
    if (!newFilteredSubCollections[0] || newFilteredSubCollections[0].typename !== 'SubCollection') return

    const respSubCollection = resp.subCollections[0]

    if (!respSubCollection) return

    if (page === 0) {
      newFilteredSubCollections[0].productCards = respSubCollection.productCards ?? []
    } else {
      let newProductCards = respSubCollection.productCards ?? []
      if (serverSubCollection && matchSubCollection(serverSubCollection, respSubCollection)) {
        newProductCards = newProductCards.filter(productCard => {
          return serverSubCollection.productCards?.every(pc => getProductKey(pc) !== getProductKey(productCard))
        })
      }
      newFilteredSubCollections[0].productCards = [
        ...(newFilteredSubCollections[0].productCards ?? []),
        ...newProductCards,
      ]
    }

    if (!respSubCollection.productCards?.length) {
      // to avoid fetching the same data again
      newFilteredSubCollections[0].totalProducts = newFilteredSubCollections[0].productCards.length
    }
  } else if (['static', 'allNewArrivals'].includes(resp.collectionType)) {
    newFilteredSubCollections.forEach(subCollection => {
      if (subCollection.typename !== 'SubCollection') return

      const respSubCollection = resp.subCollections.find(
        sc => sc.title?.trim().toLowerCase() === subCollection.title?.trim().toLowerCase(),
      )

      if (!respSubCollection) return

      if (collectionPage.collectionType === 'static' && isInitialCall) {
        subCollection.productCards = [...(respSubCollection.productCards ?? [])]
      } else {
        let newProductCards = respSubCollection.productCards ?? []
        if (serverSubCollection && matchSubCollection(serverSubCollection, respSubCollection)) {
          newProductCards = newProductCards.filter(productCard => {
            return serverSubCollection.productCards?.every(pc => getProductKey(pc) !== getProductKey(productCard))
          })
        }
        subCollection.productCards = [...(subCollection.productCards ?? []), ...newProductCards]
      }
      subCollection.totalProducts = respSubCollection.totalProducts

      if (!respSubCollection.productCards?.length) {
        // to avoid fetching the same data again
        subCollection.totalProducts = subCollection.productCards.length
      }
    })
  }
}

export function getProductTypeWithPageForStaticCollections(subCollections: SubCollection[], page: number) {
  const nextSubCollection = subCollections.find(sc => {
    return sc.totalProducts === undefined || sc.productCards.length < sc.totalProducts
  })

  if (!nextSubCollection) {
    return { doNotFetch: true }
  }

  if (!nextSubCollection.totalProducts) return { page: 0, productType: nextSubCollection.title }

  return { page, productType: nextSubCollection.title }
}

export function splitCollectionSubSections(subSections: (SubCollection | CustomSection)[]) {
  const subCollections: SubCollection[] = []
  const customSections: CustomSection[] = []
  subSections.forEach(subSection => {
    if (subSection.typename === 'SubCollection') {
      subCollections.push(subSection)
    } else {
      customSections.push(subSection as CustomSection)
    }
  })
  return { subCollections, customSections }
}

export function isEmptyFeedResponse(feedResponse?: IProductFeedResponseForCollection): boolean {
  const totalProductsInResponse = feedResponse?.subCollections.reduce(
    (count, subCollection) => count + (subCollection.productCards?.length ?? 0),
    0,
  )

  return !feedResponse?.next_url && !totalProductsInResponse
}
